import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MobileHeader, SectionNav, Card, TextInput, Textarea, Button, Badge, Select,
  SegmentedControl, FilterChip, SaveBar, Alert, ErrorState,
  useToast, useTheme,
} from '@parsina/framework';
import { THEME_PRESETS, FONT_SIZES } from '@parsina/framework/tokens';
import { Info, LogOut } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { useProfile, JOIN_CLINIC } from '../../hooks/useProfile.js';
import { logout } from '../../lib/auth.js';
import { bffMutation, errorCode } from '../../lib/bffClient.js';
import { sortPresences, presenceDayKey, minutesToClock } from '../../lib/presence.js';
import {
  PROFILE_SECTIONS, PROFILE_SECTION_IDS, DEFAULT_PROFILE_SECTION,
  snapshot, changedOnly, isDirty, isMedicalCodeLocked, languageOptions,
} from './Profile.shared.js';
import { MobileSectionTitle, MobileInfoRow } from '../../components/mobile/MobileKit.jsx';
import { ProfileSkeletonMobile } from './Profile.skeleton.jsx';

// پروفایل — a top-level mobile destination, sectioned.
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — cockpit              │  avatar · greeting · name
// │ ② SectionNav — STICKY                 │  هویت · کلینیک‌ها · شیفت‌ها · ظاهر
// │ ③ the active section                  │
// │ [ SaveBar — only while dirty ]         │  identity section only
// │ [ BottomNav ]                          │
// └──────────────────────────────────────┘
//
// ── THIS SCREEN CLOSES THE TWO HOLES THE MOBILE FORK CREATES ────────────────
//
// The desktop SIDEBAR owns the clinic switcher and sign-out, and there is no
// sidebar on a phone. Without them:
//
//   • a multi-clinic dentist would be locked to one scope forever, with امروز
//     and بیماران silently showing one clinic and no way to say which;
//   • there would be no way to sign out at all.
//
// Both live in this screen now. The switcher lists `scopeClinics`, NOT
// `clinics`: a pending membership cannot scope anything (the BFF answers
// CLINIC_FORBIDDEN), so offering it would break every screen and then snap back.
// That reasoning is the sidebar's and it survives the port.
//
// The desktop's four sections overflow a `SegmentedControl` at 320px, which is
// why this uses the kit's scrolling `SectionNav` instead (§4C).

const ProfileMobile = () => {
  const { t, locale, setLocale, formatDigits } = useTranslation();
  const toast = useToast();
  const { setAppearance } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinics, scopeClinics, activeId, setActiveId, reload: reloadClinics } = useClinicScope();
  const { me, appearance, presences, loading, error, reload, saveProfile, saveAppearance } = useProfile();

  const section = PROFILE_SECTION_IDS.includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : DEFAULT_PROFILE_SECTION;

  const setSection = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_PROFILE_SECTION) params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [form, setForm] = React.useState(() => snapshot(null));
  const [saved, setSaved] = React.useState(() => snapshot(null));
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  React.useEffect(() => {
    if (!me) return;
    const snap = snapshot(me);
    setForm(snap);
    setSaved(snap);
  }, [me]);

  const dirty = isDirty(form, saved);
  const codeLocked = isMedicalCodeLocked(saved);

  const submit = async () => {
    if (!form.fullName?.trim()) {
      setFormError(t('validation.fullName'));
      return;
    }
    // Unreachable while the field is locked (which is whenever `saved` holds a
    // number) — it guards the account that has none and starts typing one.
    if (saved.medicalCode && !form.medicalCode?.trim()) {
      setFormError(t('validation.medicalCode'));
      return;
    }
    const changes = changedOnly(form, saved);
    if (Object.keys(changes).length === 0) {
      toast.info(t('profile.noChanges'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveProfile(changes);
      toast.success(t('profile.saved'));
    } catch (err) {
      const msg =
        errorCode(err) === 'DUPLICATE_MEDICAL_CODE'
          ? t('profile.duplicateCode')
          : t('profile.saveFailed');
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Paint first, persist second, revert the paint on failure — the whole point
  // of an accent picker is seeing the accent.
  const commitAppearance = async (patch) => {
    const before = { theme: appearance?.theme, fontSize: appearance?.fontSize };
    if (patch.theme || patch.fontSize) setAppearance(patch);
    try {
      await saveAppearance(patch);
      toast.success(t('profile.appearance.saved'));
    } catch {
      if (patch.theme || patch.fontSize) setAppearance(before);
      toast.error(t('profile.appearance.failed'));
    }
  };

  // Language switches the UI FIRST — nothing unmounts, so the next frame is
  // already in the new language and the confirmation toast arrives in it too.
  const commitLanguage = async (next) => {
    const previous = locale;
    setLocale(next);
    try {
      await saveAppearance({ language: next });
    } catch {
      setLocale(previous);
      toast.error(t('profile.appearance.failed'));
    }
  };

  const [code, setCode] = React.useState('');
  const [joining, setJoining] = React.useState(false);
  const [joinError, setJoinError] = React.useState(null);

  const join = async () => {
    const value = code.trim();
    if (!value) {
      setJoinError(t('validation.invitationCode'));
      return;
    }
    setJoining(true);
    setJoinError(null);
    try {
      const res = await bffMutation(JOIN_CLINIC, { invitationCode: value });
      const name = res?.joinClinicByCode?.clinic?.name || value;
      toast.success(t('profile.clinics.joined', { name }));
      setCode('');
      reloadClinics();
    } catch (err) {
      const msg =
        errorCode(err) === 'CLINIC_NOT_FOUND'
          ? t('profile.clinics.notFound')
          : t('profile.clinics.joinFailed');
      setJoinError(msg);
    } finally {
      setJoining(false);
    }
  };

  const sections = React.useMemo(() => PROFILE_SECTIONS(t), [t]);
  const navItems = React.useMemo(
    () => sections.map(({ value, label, Icon }) => ({
      key: value,
      label,
      icon: <Icon size={16} aria-hidden />,
    })),
    [sections],
  );

  // The page's OWN mobile shape — the greeting band, the section navigator
  // and the body of the section the URL names — never a generic card stack
  // (DESIGN.md §"Loading & skeleton").
  if (loading && !me) {
    return <ProfileSkeletonMobile section={section} label={t('common.loading')} />;
  }

  if (error && !me) {
    return (
      <div data-testid="profile-content" data-state="error" role="alert" style={{ padding: '0.875rem 0.75rem' }}>
        <ErrorState
          title={t('errors.load.profile')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  const sortedPresences = sortPresences(presences);

  return (
    <div
      data-testid="profile-content"
      data-state="content"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {/* ONE scroll region, with the header inside it: the greeting scrolls away
          and the section navigator is what stays (§3.2 — a content screen may
          let the header scroll; content wins the viewport). */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <MobileHeader
          user={{ name: me?.fullName || '' }}
          greeting={t('mobile.greeting')}
          subtitle={me?.specialty || undefined}
          data-testid="profile-header"
        />

        <div style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <SectionNav
            items={navItems}
            active={section}
            onSelect={setSection}
            ariaLabel={t('profile.sections.label')}
            data-testid="profile-nav"
          />
        </div>

        {/* The kit's SectionNav declares role="tablist"/role="tab"; this is the
            other half of the pattern. */}
        <div
          role="tabpanel"
          aria-label={sections.find((s) => s.value === section)?.label}
          style={{ padding: '0.875rem 0.75rem 1.5rem' }}
        >
          {/* ── هویت ─────────────────────────────────────────────────────── */}
          {section === 'identity' && (
            <>
              {/* role="alert": the message appears after a tap and the dentist's
                  focus is on the docked ذخیره, nowhere near this line. */}
              {formError && (
                <Alert variant="error" role="alert" style={{ marginBottom: '0.875rem' }}>
                  {formError}
                </Alert>
              )}

              <Card style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {/* aria-label on every field: the kit's FormField renders its
                    label as a <span> with no htmlFor/id, so the visible label is
                    not attached to the control. dir="auto", not the kit's
                    hardcoded "rtl": a Latin name in a forced-RTL box is
                    caret-reordered as it is typed. ≥1rem stops iOS zooming. */}
                <TextInput
                  label={t('profile.fields.fullName')}
                  aria-label={t('profile.fields.fullName')}
                  dir="auto"
                  required
                  aria-required="true"
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  data-testid="profile-fullname"
                  inputStyle={{ fontSize: '1rem' }}
                />
                {/* Read-only once stated — see Profile.web.jsx and
                    Profile.shared.js; the BFF drops it on save either way. */}
                <TextInput
                  label={t('profile.fields.medicalCode')}
                  aria-label={t('profile.fields.medicalCode')}
                  dir="ltr"
                  inputMode="numeric"
                  value={form.medicalCode}
                  onChange={codeLocked
                    ? () => {}
                    : (e) => setForm((f) => ({ ...f, medicalCode: e.target.value }))}
                  disabled={codeLocked}
                  helperText={codeLocked ? t('profile.medicalCodeLocked') : undefined}
                  data-testid="profile-medicalcode"
                  inputStyle={{ fontSize: '1rem' }}
                />
                {/* The STORED value wins — a real row may hold
                    'متخصص دندانپزشکی', and showing a tidied-up label instead
                    would hide what the account actually carries. */}
                <TextInput
                  label={t('profile.fields.specialty')}
                  aria-label={t('profile.fields.specialty')}
                  dir="auto"
                  value={me?.specialty || t('profile.specialtyValue')}
                  onChange={() => {}}
                  disabled
                  helperText={t('profile.specialtyLocked')}
                  inputStyle={{ fontSize: '1rem' }}
                />
                <Textarea
                  label={t('profile.fields.bio')}
                  aria-label={t('profile.fields.bio')}
                  dir="auto"
                  rows={3}
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  data-testid="profile-bio"
                  inputStyle={{ fontSize: '1rem' }}
                />
              </Card>

              {/* Sign-out. It lives in the sidebar on the desktop and there is
                  no sidebar here, so without this the phone has no way out of
                  the account at all. Deliberately separated from the commit row
                  by a rule and a gap so it can never be mis-tapped for ذخیره,
                  and deliberately NOT confirm-gated — signing out destroys
                  nothing, and the desktop does not confirm it either. */}
              <div
                style={{
                  marginTop: '1.5rem',
                  paddingTop: '1rem',
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <Button
                  variant="outline"
                  onClick={() => logout()}
                  data-testid="profile-signout"
                  className="w-full min-h-11 gap-1.5"
                >
                  <LogOut size={16} aria-hidden />
                  {t('nav.sidebar.logout')}
                </Button>
              </div>
            </>
          )}

          {/* ── کلینیک‌ها ─────────────────────────────────────────────────── */}
          {section === 'clinics' && (
            <>
              {/* The scope switcher. Shown only when there is a choice to make:
                  with one membership the scope is not a decision, and with none
                  the empty state on بیماران explains it far better than a
                  disabled dropdown would. */}
              {scopeClinics.length > 1 && (
                <Card style={{ padding: '0.875rem', marginBottom: '0.875rem' }}>
                  <Select
                    label={t('profile.clinics.activeScope')}
                    aria-label={t('profile.clinics.activeScope')}
                    value={activeId || ''}
                    // The kit's Select hands its handler the DOM EVENT, not the
                    // value — it is a native <select> underneath.
                    onChange={(e) => setActiveId(e.target.value)}
                    options={scopeClinics.map((m) => ({
                      value: m.clinicId,
                      label: m.clinic?.name || m.clinicId,
                    }))}
                    // The kit's default placeholder is a Persian literal, and
                    // `activeId` always resolves to one of these clinics
                    // (clinicScopeState falls back to the first usable one), so
                    // an empty option would be an unreachable state in `en`.
                    placeholder=""
                    data-testid="profile-clinic-scope"
                  />
                  <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.5rem' }}>
                    {t('profile.clinics.activeScopeHint')}
                  </div>
                </Card>
              )}

              <MobileSectionTitle>{t('profile.sections.clinics')}</MobileSectionTitle>
              {clinics.length === 0 ? (
                <Card style={{ padding: '0.875rem' }}>
                  <div style={{ fontSize: '0.8125rem', color: C.textMuted }}>
                    {t('profile.clinics.empty')}
                  </div>
                </Card>
              ) : (
                clinics.map((m) => (
                  <Card
                    key={m.id}
                    style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span dir="auto" style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                        {m.clinic?.name || m.clinicId}
                      </span>
                      <Badge
                        variant={m.status === 'pending' ? 'warning' : 'success'}
                        className="ms-auto"
                      >
                        {m.status === 'pending'
                          ? t('profile.clinics.status.pending')
                          : t('profile.clinics.status.active')}
                      </Badge>
                    </div>
                    {(m.clinic?.city || m.visitDurationMinutes) && (
                      <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.25rem' }}>
                        {[
                          m.clinic?.city || '',
                          m.visitDurationMinutes
                            ? t('profile.clinics.visitDuration', { count: m.visitDurationMinutes })
                            : '',
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </Card>
                ))
              )}

              <MobileSectionTitle style={{ marginTop: '0.875rem' }}>
                {t('profile.clinics.joinTitle')}
              </MobileSectionTitle>
              <Card style={{ padding: '0.875rem' }}>
                <div style={{ fontSize: '0.75rem', color: C.textMuted, marginBottom: '0.75rem' }}>
                  {t('profile.clinics.joinHint')}
                </div>
                {joinError && (
                  <Alert variant="error" role="alert" style={{ marginBottom: '0.75rem' }}>
                    {joinError}
                  </Alert>
                )}
                <TextInput
                  label={t('profile.clinics.joinPlaceholder')}
                  aria-label={t('profile.clinics.joinPlaceholder')}
                  dir="ltr"
                  value={code}
                  // A one-field form: Enter submits it.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !joining) {
                      e.preventDefault();
                      join();
                    }
                  }}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="profile-join-code"
                  inputStyle={{ fontSize: '1rem' }}
                />
                <Button
                  onClick={join}
                  disabled={joining}
                  data-testid="profile-join"
                  className="mt-3 w-full min-h-11"
                >
                  {joining ? t('common.actions.saving') : t('common.actions.join')}
                </Button>
              </Card>
            </>
          )}

          {/* ── شیفت‌ها ───────────────────────────────────────────────────── */}
          {section === 'presence' && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.6875rem',
                  color: C.textMuted,
                  lineHeight: 1.8,
                }}
              >
                <Info size={14} style={{ flexShrink: 0, marginTop: '0.125rem' }} aria-hidden />
                <span>{t('profile.presence.readOnly')}</span>
              </div>
              {sortedPresences.length === 0 ? (
                <Card style={{ padding: '0.875rem' }}>
                  <div style={{ fontSize: '0.8125rem', color: C.textMuted }}>
                    {t('profile.presence.empty')}
                  </div>
                </Card>
              ) : (
                <Card style={{ padding: '0.25rem 0.875rem' }}>
                  {sortedPresences.map((p) => {
                    const dayKey = presenceDayKey(p.dayOfWeek);
                    const clinic = clinics.find((c) => c.clinicId === p.clinicId);
                    return (
                      <MobileInfoRow
                        key={p.id}
                        label={(
                          <>
                            {dayKey ? t(dayKey) : t('profile.presence.oneOff')}
                            {/* A disabled shift drawn at 55% opacity and nothing
                                else is a state a screen reader cannot report. */}
                            {!p.isActive && (
                              <Badge variant="outline" className="ms-1.5">
                                {t('profile.presence.inactive')}
                              </Badge>
                            )}
                          </>
                        )}
                        value={(
                          <>
                            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                              {formatDigits(`${minutesToClock(p.startTimeMinutes)} – ${minutesToClock(p.endTimeMinutes)}`)}
                            </span>
                            {clinic && (
                              <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                                {clinic.clinic?.name || clinic.clinicId}
                              </div>
                            )}
                          </>
                        )}
                      />
                    );
                  })}
                </Card>
              )}
            </>
          )}

          {/* ── ظاهر ─────────────────────────────────────────────────────── */}
          {section === 'appearance' && (
            <>
              <MobileSectionTitle>{t('profile.appearance.theme')}</MobileSectionTitle>
              <Card style={{ padding: '0.875rem' }}>
                <div
                  role="group"
                  aria-label={t('profile.appearance.theme')}
                  style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
                >
                  {THEME_PRESETS.map((preset) => (
                    // The kit FilterChip, not the desktop's hand-rolled raw
                    // <button> swatches: min-h-11, aria-pressed, data-state.
                    // The swatch rides in as `children`; the LABEL is the
                    // catalogue's, because the kit's preset labels are
                    // Persian-only literals.
                    <FilterChip
                      key={preset.id}
                      active={(appearance?.theme || 'default') === preset.id}
                      onClick={() => commitAppearance({ theme: preset.id })}
                      data-testid={`profile-theme-${preset.id}`}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: '0.875rem',
                          height: '0.875rem',
                          borderRadius: '50%',
                          display: 'inline-block',
                          background: `linear-gradient(135deg, ${preset.swatch}, ${preset.swatch2})`,
                        }}
                      />
                      {t(`profile.theme.${preset.id}`)}
                    </FilterChip>
                  ))}
                </div>
              </Card>

              <MobileSectionTitle style={{ marginTop: '0.875rem' }}>
                {t('profile.appearance.fontSize')}
              </MobileSectionTitle>
              <Card style={{ padding: '0.875rem' }}>
                {/* size="lg" is the kit's 44px form — the default `md` is 36px
                    and below the touch floor (§6). */}
                <SegmentedControl
                  size="lg"
                  options={FONT_SIZES.map((f) => ({ value: f.id, label: t(`profile.fontSize.${f.id}`) }))}
                  value={appearance?.fontSize || 'medium'}
                  onChange={(v) => commitAppearance({ fontSize: v })}
                />
              </Card>

              <MobileSectionTitle style={{ marginTop: '0.875rem' }}>
                {t('profile.appearance.language')}
              </MobileSectionTitle>
              <Card style={{ padding: '0.875rem' }}>
                {/* SegmentedControl has no overflow strategy, and four endonyms
                    (with «Deutsch», PM-0212) are wider than a phone card. The
                    strip scrolls inside the card, never the page
                    (MOBILE_WEB_PWA.md). Where the options fit, nothing changes. */}
                <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <SegmentedControl
                    size="lg"
                    options={languageOptions(t)}
                    value={locale}
                    onChange={commitLanguage}
                  />
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* The commit row appears ONLY while the identity form is dirty — the
          kit's stated SaveBar contract — and docks at the bottom, above the
          bottom navigation. */}
      {section === 'identity' && dirty && (
        <SaveBar
          sticky
          onSave={submit}
          onCancel={() => { setForm(saved); setFormError(null); }}
          saving={saving}
          saveLabel={t('common.actions.save')}
          cancelLabel={t('common.actions.cancel')}
          data-testid="profile-savebar"
          style={{ marginTop: 0, flexShrink: 0, paddingInline: '0.75rem' }}
        />
      )}
    </div>
  );
};

export default ProfileMobile;
